import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { LocationInput } from '@/components/ui/location-input';
import { updateGateway } from '@/features/gateways/api/gateways-api';
import type { Gateway } from '@/types/api';

interface GatewayLocationDialogProps {
  gateway: Gateway;
  onClose: () => void;
  onSaved: (updated: Gateway) => void;
}

export function GatewayLocationDialog({ gateway, onClose, onSaved }: GatewayLocationDialogProps) {
  const [latitude, setLatitude] = useState(gateway.latitude?.toString() ?? '');
  const [longitude, setLongitude] = useState(gateway.longitude?.toString() ?? '');
  const [shouldClearLocation, setShouldClearLocation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasExistingLocation = gateway.latitude != null && gateway.longitude != null;

  function handleClearLocation() {
    setLatitude('');
    setLongitude('');
    setShouldClearLocation(true);
    setError(null);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      if (shouldClearLocation) {
        // Backend nulls Latitude/Longitude when ClearLocation is true; the
        // (latitude, longitude) pair is omitted entirely.
        const res = await updateGateway(gateway.id, { clearLocation: true });
        if (!res.success) {
          setError(res.message || 'Failed to clear location.');
          return;
        }
        onSaved({ ...gateway, latitude: undefined, longitude: undefined });
        return;
      }
      const lat = latitude ? parseFloat(latitude) : undefined;
      const lng = longitude ? parseFloat(longitude) : undefined;
      if (lat === undefined || lng === undefined || Number.isNaN(lat) || Number.isNaN(lng)) {
        setError('Enter both latitude and longitude, or cancel.');
        return;
      }
      const res = await updateGateway(gateway.id, { latitude: lat, longitude: lng });
      if (!res.success) {
        setError(res.message || 'Failed to save location.');
        return;
      }
      onSaved({ ...gateway, latitude: lat, longitude: lng });
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Failed to save location.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={gateway.latitude != null ? 'Edit Gateway Location' : 'Set Gateway Location'}
      description={`${gateway.name} — ${gateway.gatewayEui}`}
    >
      <div className="space-y-4">
        <LocationInput
          latitude={latitude}
          longitude={longitude}
          onChange={({ latitude: lat, longitude: lng }) => {
            setLatitude(lat);
            setLongitude(lng);
            // Editing pulls the dialog out of "clear" mode — the user clearly
            // wants to set a new value, not remove it.
            if (lat || lng) setShouldClearLocation(false);
          }}
        />
        {shouldClearLocation && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Location will be removed when you save.
          </p>
        )}
        {hasExistingLocation && !shouldClearLocation && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClearLocation}
            data-testid="clear-location-button"
            className="text-error-600 hover:text-error-700"
          >
            <X className="w-3.5 h-3.5" />
            Clear location
          </Button>
        )}
        {error && <p className="text-sm text-error-600">{error}</p>}
        <div className="flex gap-3 pt-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handleSave} isLoading={saving} className="flex-1">
            {shouldClearLocation ? 'Remove Location' : 'Save Location'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
